import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

const ELIGIBLE_KEY = "crt_guide_tour_eligible_v1";
const SEEN_KEY = "crt_guide_tour_seen_v1";

const STEPS = [
  {
    title: "WELCOME TO THE CHANNEL GUIDE",
    body: "This is the live television schedule. Every row is a channel, every block is a real scheduled programme, and the timeline keeps moving with the broadcast clock.",
    selector: null,
    welcome: true,
  },
  {
    title: "SEARCH SHOWS OR CHANNELS",
    body: "Search from here for a channel or programme. Press / from anywhere in SCARTLEAD.TV to open the Guide with search ready.",
    selector: '[data-testid="guide-search-banner"]',
  },
  {
    title: "THE LIVE TIMELINE",
    body: "The main grid is the FULL GUIDE. Programme widths reflect their real duration, so a two-hour film occupies more timeline space than a short episode.",
    selector: '[data-testid="guide-full-timeline"]',
  },
  {
    title: "CLOCK + NOW",
    body: "The ruler shows broadcast time. The vertical NOW line marks the exact current position across every channel, so you can immediately see how far through each live programme you are.",
    selector: '[data-testid="guide-clock-ruler"]',
    clipSelector: '[data-testid="guide-full-list"]',
  },
  {
    title: "BROWSE THE SCHEDULE",
    body: "Use LEFT and RIGHT to move through real programme blocks. Use UP and DOWN to move between channels while preserving the same point in time. The Guide scrolls automatically to keep your selection visible.",
    selector: '[data-testid="guide-full-list"]',
  },
  {
    title: "PROGRAMME DETAILS",
    body: "The information panel follows the highlighted programme with its title, synopsis and artwork. Browsing here does not change what is playing on the TV.",
    selector: '[data-testid="guide-full-selection"]',
  },
  {
    title: "WATCH NOW",
    body: "WATCH NOW is the playback commit. If the selected programme is live, you join it at the current broadcast position. Past or future programme selections are explicit viewing and start from the beginning.",
    selector: '[data-testid="guide-full-watch"]',
  },
  {
    title: "FEATURED",
    body: "FEATURED surfaces curated picks from across the schedule. Use it when you want something worth discovering without browsing every channel one programme at a time.",
    selector: '[data-testid="guide-full-featured-toggle"]',
  },
  {
    title: "PROGRAMMES",
    body: "PROGRAMMES switches the discovery strip to the selected channel's playlist, giving you a quick way to browse that channel's other programmes. Selecting one updates the Guide first; playback still waits for WATCH NOW.",
    selector: '[data-testid="guide-full-programmes-toggle"]',
  },
  {
    title: "YOU'RE READY",
    body: "Browse freely without disturbing the current broadcast. Follow the NOW line for live television, use FEATURED when you want a recommendation, and use WATCH NOW only when you are ready to tune.",
    selector: null,
    finish: true,
  },
];

const PAD = 8;

export default function GuideFirstVisitTour({ enabled = true }) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);

  const step = STEPS[index];
  const total = STEPS.length;

  // The Guide component only mounts while the Guide is open. This makes the
  // eligibility check naturally "first time the viewer visits the Guide".
  // Eligibility itself is written by the refreshed main FirstVisitTour, which
  // prevents existing viewers from being treated as brand-new visitors.
  useEffect(() => {
    if (!enabled) return;
    try {
      const eligible = localStorage.getItem(ELIGIBLE_KEY) === "1";
      const seen = localStorage.getItem(SEEN_KEY) === "1";
      if (!eligible || seen) return;
    } catch {
      return;
    }

    const timer = setTimeout(() => {
      setIndex(0);
      setOpen(true);
    }, 450);

    return () => clearTimeout(timer);
  }, [enabled]);

  const finish = () => {
    try {
      localStorage.setItem(SEEN_KEY, "1");
      localStorage.removeItem(ELIGIBLE_KEY);
    } catch { /* private mode / quota — close for this mount anyway */ }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setRect(null);
      return undefined;
    }

    let raf = 0;
    const measure = () => {
      if (!step?.selector) {
        setRect(null);
        return;
      }

      const el = document.querySelector(step.selector);
      if (!el) {
        setRect(null);
        return;
      }

      const r = el.getBoundingClientRect();
      let top = r.top;
      let left = r.left;
      let right = r.right;
      let bottom = r.bottom;

      // Phase 2.3.62.5-r2.1 — some Guide targets (notably the proportional
      // clock ruler) are intentionally wider than their scroll viewport.
      // A tour spotlight should describe the visible UI, not the off-screen
      // portion of a horizontally scrolling canvas.
      if (step.clipSelector) {
        const clipEl = document.querySelector(step.clipSelector);
        if (clipEl) {
          const c = clipEl.getBoundingClientRect();
          top = Math.max(top, c.top);
          left = Math.max(left, c.left);
          right = Math.min(right, c.right);
          bottom = Math.min(bottom, c.bottom);
        }
      }

      if (right <= left || bottom <= top) {
        setRect(null);
        return;
      }

      const paddedTop = Math.max(0, top - PAD);
      const paddedLeft = Math.max(0, left - PAD);
      const paddedRight = Math.min(window.innerWidth, right + PAD);
      const paddedBottom = Math.min(window.innerHeight, bottom + PAD);

      setRect({
        top: paddedTop,
        left: paddedLeft,
        right: paddedRight,
        bottom: paddedBottom,
        width: Math.max(0, paddedRight - paddedLeft),
        height: Math.max(0, paddedBottom - paddedTop),
      });
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    const interval = setInterval(measure, 300);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(interval);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
    };
  }, [open, index, step?.selector]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        finish();
      } else if ((e.key === "ArrowRight" || e.key === "Enter") && !step?.finish) {
        e.preventDefault();
        e.stopPropagation();
        setIndex((i) => Math.min(total - 1, i + 1));
      } else if (e.key === "ArrowLeft" && index > 0) {
        e.preventDefault();
        e.stopPropagation();
        setIndex((i) => Math.max(0, i - 1));
      }
    };

    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, index, step?.finish, total]);

  const calloutStyle = useMemo(() => {
    if (!rect) {
      return {
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(390px, calc(100vw - 24px))",
      };
    }

    const width = Math.min(390, Math.max(300, window.innerWidth - 24));
    const margin = 18;
    const spaceRight = window.innerWidth - rect.right;
    const spaceLeft = rect.left;

    let left;
    if (spaceRight >= width + margin) left = rect.right + margin;
    else if (spaceLeft >= width + margin) left = rect.left - width - margin;
    else left = Math.max(12, Math.min(window.innerWidth - width - 12, rect.left));

    const top = Math.max(12, Math.min(window.innerHeight - 250, rect.top));
    return { left, top, width };
  }, [rect]);

  if (!open || typeof document === "undefined") return null;

  const blockers = rect
    ? [
        { top: 0, left: 0, right: 0, height: rect.top },
        { top: rect.bottom, left: 0, right: 0, bottom: 0 },
        { top: rect.top, left: 0, width: rect.left, height: rect.height },
        { top: rect.top, left: rect.right, right: 0, height: rect.height },
      ]
    : [{ inset: 0 }];

  return createPortal(
    <div
      className="fixed inset-0 z-[110]"
      data-testid="guide-first-visit-tour"
      style={{ fontFamily: "JetBrains Mono, monospace" }}
      aria-live="polite"
    >
      {blockers.map((style, i) => (
        <div
          key={i}
          className="fixed bg-black/78 backdrop-blur-[1px]"
          style={style}
        />
      ))}

      {rect && (
        <>
          <div
            className="fixed pointer-events-none border-2 border-[var(--phosphor)] shadow-[0_0_22px_var(--phosphor)] rounded-md"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            }}
          />
          {/* Guide onboarding is explanatory rather than interactive:
              clicks stay blocked until the viewer finishes/skips. */}
          <div
            className="fixed"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            }}
          />
        </>
      )}

      <div
        className="fixed bg-black border-2 border-[var(--phosphor)] text-[var(--phosphor)] p-4 shadow-[0_0_24px_color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
        style={calloutStyle}
        data-testid="guide-first-visit-card"
      >
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-[10px] tracking-[0.24em] opacity-60">
            {step.welcome
              ? "GUIDE QUICK START"
              : step.finish
                ? "COMPLETE"
                : `${index}/${total - 2}`}
          </div>

          {!step.finish && (
            <button
              type="button"
              onClick={finish}
              className="text-[9px] tracking-[0.18em] opacity-60 hover:opacity-100"
              data-testid="guide-tour-skip"
            >
              SKIP TOUR
            </button>
          )}
        </div>

        <div
          className="text-[20px] tracking-[0.12em] mb-2"
          style={{ fontFamily: "VT323, monospace" }}
        >
          {step.title}
        </div>

        <div className="text-[11px] leading-relaxed text-neutral-200 mb-4">
          {step.body}
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0 || step.finish}
            className="text-[10px] tracking-[0.18em] border border-neutral-700 px-3 py-1.5 disabled:opacity-30"
            data-testid="guide-tour-back"
          >
            ◀ BACK
          </button>

          {step.finish ? (
            <button
              type="button"
              onClick={finish}
              className="text-[10px] tracking-[0.18em] border border-[var(--phosphor)] px-3 py-1.5 hover:bg-[var(--phosphor)] hover:text-black"
              data-testid="guide-tour-finish"
            >
              EXPLORE GUIDE ▶
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
              className="text-[10px] tracking-[0.18em] border border-[var(--phosphor)] px-3 py-1.5 hover:bg-[var(--phosphor)] hover:text-black"
              data-testid={step.welcome ? "guide-tour-start" : "guide-tour-next"}
            >
              {step.welcome ? "SHOW ME ▶" : "NEXT ▶"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
